<template>
  <div class="overflow-y-hidden w-full h-[100lvh] bg-cw-green-dark flex flex-col fixed left-0 top-0 z-50">
    <div class="px-16 py-24 heightMd:pt-8 heightSm:pt-8">
      <div class="flex flex-col justify-center items-center h-full">
        <h1 class="text-2xl heightMd:text-lg heightSm:text-[16px] font-bold text-white tracking-normal mb-10">{{ currentTurnPlayerProfile?.username }} has requested for a card</h1>
        <div class="flex gap-10 justify-center">
          <a
            href=""
            @click.prevent="selectedCardId = card!.id"
            v-for="card in availableCardsForSwap"
            class="w-full inline-flex items-center rounded-[20px] border-4 border-transparent"
            :class="{ '!border-cw-yellow': selectedCardId == card!.id && availableCardsForSwap.length > 1 }"
            :key="card!.id"
          >
            <GameCardItem :cursor="'pointer'" :card="card!" />
          </a>
        </div>
        <div class="mt-5" v-if="availableCardsForSwap.length > 1">
          <h5 class="font-semibold text-cw-grey-2 mt-2 text-xl">Pick a card to send</h5>
        </div>
        <div class="flex gap-20 mt-15">
          <BaseButton
            class="heightMd:px-9 px-16 flex items-center justify-center !rounded-xl text-lg font-bold heightMd:text-sm"
            backgroundColor="var(--color-cw-green)"
            borderColor="var(--color-cw-green-border-shadow)"
            @click="acceptTrade"
          >
            Accept
          </BaseButton>
          <BaseButton
            class="heightMd:px-9 px-16 flex items-center justify-center !rounded-xl text-lg font-bold heightMd:text-sm"
            backgroundColor="var(--color-cw-red-light)"
            borderColor="var(--color-cw-red-border-shadow)"
            @click="rejectTrade"
          >
            Reject
          </BaseButton>
        </div>
      </div>
    </div>
    <GameTimer :timeRemaining="userPlayerProfile.actionTimeout" :key="userPlayerProfile.actionTimeout" v-if="userPlayerProfile && userPlayerProfile.actionTimeout > 0" class="absolute bottom-10 left-10" />
  </div>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, ref } from 'vue'
import BaseButton from '@/components/base/BaseButton.vue'
import type { CardT } from '@/types/Game'
import { useGameStore } from '@/stores/game'
import { storeToRefs } from 'pinia'
import { useHelpers } from '@/composables/useHelpers'
import { GameEvent } from '@/types/Events'
import useSocket from '@/composables/useSocket'
import GameCardItem from '@/components/game-play/GameCardItem.vue'
import { useGameArea } from '@/composables/useGameArea'
import GameTimer from '@/components/interface/GameTimer.vue'

const showLoader = inject('showLoader') as InjectedFunctionT
const hideLoader = inject('hideLoader') as InjectedFunctionT

const { userPlayerProfile, currentTurnPlayerProfile, gameState } = storeToRefs(useGameStore())
const { handleCustomError } = useHelpers()
const socket = useSocket()
const { getCardById } = useGameStore()
const { setActiveGameAreaScreen } = useGameArea()

const selectedCardId = ref<string | null>(null)

const myOffer = computed(() => {
  return gameState.value!.activeTrade?.offers.find(offer => offer.playerId === userPlayerProfile.value!.id)
})

const availableCardsForSwap = computed(() => {
  return myOffer.value?.matchingCardIds.map(cardId => getCardById(cardId)).filter((card): card is CardT => Boolean(card && card.id && card.category)) || []
})

const acceptTrade = async () => {
  try {
    showLoader()
    setActiveGameAreaScreen('IDLE_SCREEN')
    await socket.sendEventAsync<CardT[]>(GameEvent.AcceptTrade, {
      cardId: selectedCardId.value
    })
  } catch (error: any) {
    handleCustomError(error)
  } finally {
    hideLoader()
  }
}

const rejectTrade = async () => {
  try {
    showLoader()
    setActiveGameAreaScreen('IDLE_SCREEN')
    await socket.sendEventAsync<CardT[]>(GameEvent.RejectTrade)
  } catch (error: any) {
    handleCustomError(error)
  } finally {
    hideLoader()
  }
}

onMounted(() => {
  selectedCardId.value = availableCardsForSwap.value[0]?.id || null
})
</script>

<style scoped></style>
